import { useState, type ReactNode } from 'react';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { cn } from '@unitfield/ui/lib/utils';
import shell from '../shell/shell.module.css';
import { safeCheckrApplicationUrl, type PublicScreening } from './screening-handoff';
import styles from './continuation-page.module.css';

function formatFee(cents: number | null): string | null {
  if (cents === null) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
}

const progressCopy: Record<PublicScreening['applicantProgress'], string | null> = {
  not_started: null,
  visited: 'You opened your screening on Checkr. Finish there, then come back to this page.',
  started: 'You started your screening on Checkr. Finish there, then come back to this page.',
  completed: 'Checkr received your information.',
  paid: 'Checkr received your payment.',
};

const sentence = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

function heading(screening: PublicScreening, business: string): { title: string; tone?: 'success' | 'danger'; status: string } {
  switch (screening.state) {
    case 'action_required': return { title: 'Complete your tenant screening', status: `${sentence(business)} requested a tenant screening before making a decision. Checkr runs it, connected through Unitfield.` };
    case 'waiting_on_provider': return { title: 'Your screening is being prepared', status: `${sentence(business)} is preparing your screening. You don’t need to do anything yet.` };
    case 'in_progress': return { title: 'Checkr is running your screening', status: 'You’ve done your part. Nothing else is needed from you, and this page updates on its own.' };
    case 'completed': return { title: 'Your screening is complete', tone: 'success', status: `Checkr completed your screening and shared it with ${business}.` };
    case 'needs_follow_up': return { title: 'Your screening needs attention', tone: 'danger', status: `${sentence(business)} will contact you about your screening.` };
  }
}

function Row({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return <li><UnitfieldIcon name={icon} size={18} aria-hidden="true" /><span><strong>{title}</strong><span>{children}</span></span></li>;
}

/**
 * Screening step on the applicant's status page. Checkr's consent, identity and
 * payment step can't be embedded, so it opens in a separate secure tab and this
 * page refreshes when the applicant returns. Results are never shown here.
 */
export function ContinuationScreening({ screening, businessName = 'the rental business', hydrated, onRefresh, refreshing, compact = false }: {
  screening: PublicScreening;
  /** Name of the business that requested the screening. */
  businessName?: string;
  hydrated: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  compact?: boolean;
}) {
  const [opened, setOpened] = useState(false);
  const url = screening.state === 'action_required' ? safeCheckrApplicationUrl(screening.applicationUrl) : null;
  const fee = formatFee(screening.feeCents);
  const progress = screening.state === 'action_required' ? progressCopy[screening.applicantProgress] : null;
  const copy = heading(screening, businessName);

  if (compact) {
    const actionable = screening.state === 'action_required';
    const title = screening.state === 'completed' ? 'Next: the decision' : 'Next: screening';
    const status = actionable
      ? `Checkr verifies your identity and history; ${screening.payer === 'applicant' ? fee ? `you pay Checkr the ${fee} fee directly.` : 'you pay the screening fee to Checkr.' : `${businessName} covers the fee.`}`
      : screening.state === 'completed' ? `Checkr finished your screening; wait for ${businessName}’s decision.` : copy.status;
    return <section className={styles.screening} aria-labelledby="application-screening">
      <div className={styles.nowRow}>
        <div className={styles.heading} data-level={2} data-tone={copy.tone ?? 'neutral'}>
          <h2 id="application-screening">{title}{screening.isTest ? <span className={styles.testTag}>Test screening</span> : null}</h2>
          <p role="status" aria-live="polite">{status}</p>
        </div>
        {url ? <a className={cn(shell.btn, shell.btnBrand)} href={url} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(true)}>
          {screening.applicantProgress === 'not_started' ? 'Start screening' : 'Resume screening'}
          <span className="sr-only"> (opens in a new tab)</span>
        </a> : null}
      </div>
      {actionable && !url ? <div className={styles.handoff}>
        <p className={styles.progress}>Your secure screening link isn’t available on this page right now. Check your email for a message from Checkr, or refresh in a moment.</p>
        <button type="button" className={cn(shell.btn, shell.btnTonal, shell.btnSmall)} disabled={!hydrated || refreshing} onClick={onRefresh}>Refresh screening</button>
      </div> : null}
      {progress ? <p className={styles.progress}>{progress}</p> : null}
      {opened ? <p className={styles.hint}>Checkr opened in a new tab. When you finish there, return to this page. It updates automatically.</p> : null}
      {actionable ? <details className={styles.screeningDetails}>
        <summary>About screening with Checkr<UnitfieldIcon name="chevron-down" size={16} aria-hidden="true" /></summary>
        <div className={styles.nextSteps}>
          <ul>
            <Row icon="document-check" title="Give consent">Review the screening disclosures and agree to them.</Row>
            <Row icon="user-id" title="Confirm your identity">If a Social Security number is needed, you enter it with Checkr. Unitfield doesn’t ask for it.</Row>
            <Row icon="bank" title="Connect a bank, if asked">Some screenings confirm income through a bank connection. It’s optional.</Row>
            {screening.payer === 'applicant'
              ? <Row icon="credit-card" title="Pay the screening fee">{fee ? `Screening fee: ${fee}, paid to Checkr.` : 'You pay the screening fee to Checkr.'}</Row>
              : <Row icon="check-circle" title="No cost to you">{sentence(businessName)} pays for this screening.</Row>}
          </ul>
        </div>
        <p className={styles.privacy}><UnitfieldIcon name="lock" size={15} aria-hidden="true" /><span>Checkr shares the results with {businessName}. They don’t appear on this page.</span></p>
      </details> : null}
      {screening.isTest ? <p className={styles.testNote}>This is a test. No real background check is run and no one is charged.</p> : null}
    </section>;
  }

  return <section className={styles.screening} aria-labelledby="application-screening">
    <div className={styles.heading} data-level={2} data-tone={copy.tone ?? 'neutral'}>
      <h2 id="application-screening">{copy.title}{screening.isTest ? <span className={styles.testTag}>Test screening</span> : null}</h2>
      <p role="status" aria-live="polite">{copy.status}</p>
    </div>

    {screening.state === 'action_required' ? <>
      <div className={styles.nextSteps}>
        <p className={styles.nextTitle}>What you’ll do on Checkr</p>
        <ul>
          <Row icon="document-check" title="Give consent">Review the screening disclosures and agree to them.</Row>
          <Row icon="user-id" title="Confirm your identity"><span>If a Social Security number is needed, you enter it with Checkr. Unitfield doesn’t ask for it.</span></Row>
          <Row icon="bank" title="Connect a bank, if asked">Some screenings confirm income through a bank connection. It’s optional.</Row>
          {screening.payer === 'applicant'
            ? <Row icon="credit-card" title="Pay the screening fee"><span>{fee ? `Screening fee: ${fee}, paid to Checkr.` : 'You pay the screening fee to Checkr.'}</span></Row>
            : <Row icon="check-circle" title="No cost to you">{sentence(businessName)} pays for this screening.</Row>}
        </ul>
      </div>
      {progress ? <p className={styles.progress}>{progress}</p> : null}
      {url ? <div className={styles.handoff}>
        <a className={cn(shell.btn, shell.btnBrand)} href={url} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(true)}>
          Continue to Checkr<UnitfieldIcon name="external-link" size={16} aria-hidden="true" />
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
        <p className={styles.hint}>
          {opened
            ? 'Checkr opened in a new tab. When you finish there, return to this page. It updates automatically.'
            : 'Checkr opens in a new tab. Keep this page open; it updates when you come back. Checkr also emailed you this link.'}
        </p>
      </div> : <div className={styles.handoff}>
        <p className={styles.progress}>Your secure screening link isn’t available on this page right now. Check your email for a message from Checkr, or refresh in a moment.</p>
        <button type="button" className={cn(shell.btn, shell.btnTonal, shell.btnSmall)} disabled={!hydrated || refreshing} onClick={onRefresh}>Refresh screening</button>
      </div>}
      <p className={styles.privacy}><UnitfieldIcon name="lock" size={15} aria-hidden="true" /><span>Checkr shares the results with {businessName}. They don’t appear on this page.</span></p>
    </> : null}
    {screening.isTest ? <p className={styles.testNote}>This is a test. No real background check is run and no one is charged.</p> : null}

    {screening.state === 'in_progress' || screening.state === 'waiting_on_provider' ? <div className={styles.nextSteps}>
      <p className={styles.nextTitle}>What happens next</p>
      <ul>
        {screening.state === 'waiting_on_provider' ? <Row icon="mail" title="You get a link from Checkr">When it’s ready, Continue to Checkr appears here and Checkr emails you.</Row> : <Row icon="clock" title="Checkr finishes the screening">This page updates when it’s done.</Row>}
        <Row icon="user-search" title={`${sentence(businessName)} reviews your application`}>The decision comes from them, not from Unitfield or Checkr.</Row>
      </ul>
    </div> : null}

    {screening.state === 'completed' ? <div className={styles.nextSteps}>
      <p className={styles.nextTitle}>What happens next</p>
      <ul>
        <Row icon="user-search" title="They review your application">Your application and screening are looked at together.</Row>
        <Row icon="mail" title="The decision comes from them">You hear back from {businessName} directly. Unitfield and Checkr don’t decide.</Row>
      </ul>
    </div> : null}
  </section>;
}
